import React, { useEffect } from "react";
import "./AutomaticBankReconciliationPage.scss";
import heroBanner from "../../assets/images/banner-automatic-bank-reconciliation.png";
import Copyright from "../../components/Copyright/Copyright";
import { Link } from "react-router-dom";

const AutomaticBankReconciliationPage = () => {
  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="feature-page">
      {/* Hero Section */}
      <section className="feature-hero">
        <div className="container">
          <div className="feature-hero-image">
            <img src={heroBanner} alt="Automatic Bank Reconciliation" />
          </div>
        </div>
      </section>

      {/* Automatic Bank Reconciliation Information */}
      <section className="feature-info">
        <div className="container">
          <div className="feature-grid">
            <div className="feature-details">
              <h2>Automatic bank reconciliation</h2>
              <p>
                Streamline your financial processes with CashManager's intelligent bank reconciliation system.
                Our advanced algorithms automatically match transactions, identify discrepancies, and ensure
                your books are always accurate and up-to-date.
              </p>

              <div className="feature-methods">
                <div className="feature-method">
                  <div className="method-content">
                    <h3>Smart matching algorithms</h3>
                    <p>
                      Advanced pattern recognition automatically matches bank transactions with your
                      accounting records using multiple criteria and fuzzy matching techniques.
                    </p>
                  </div>
                </div>

                <div className="feature-method">
                  <div className="method-content">
                    <h3>Customizable rules</h3>
                    <p>
                      Define your own matching rules and criteria to handle specific business
                      scenarios and ensure accurate reconciliation for your unique processes.
                    </p>
                  </div>
                </div>

                {/* <div className="reconciliation-method">
                  <div className="method-content">
                    <h3>Exception handling</h3>
                    <p>
                      Intelligent exception management identifies unmatched transactions and
                      provides clear guidance for manual review and resolution.
                    </p>
                  </div>
                </div> */}

              </div>
            </div>

            <div className="feature-features">
              <h2>Key features</h2>
              <div className="features-list">
                <div className="feature-item">
                  <h3>Multi-criteria matching</h3>
                  <p>Match transactions using any possible criteria - amount, date, beneficiary data, reference numbers, description, etc., and custom business rules.</p>
                </div>

                <div className="feature-item">
                  <h3>Multi-currency support</h3>
                  <p>Handle reconciliation across multiple currencies with automatic exchange rate conversion.</p>
                </div>

                {/* <div className="feature-item">
                  <h3>Real-time processing</h3>
                  <p>Process bank statements and reconcile transactions as they occur for immediate accuracy.</p>
                </div> */}

                <div className="feature-item">
                  <h3>Bulk reconciliation</h3>
                  <p>Process large volumes of transactions efficiently with batch reconciliation capabilities.</p>
                </div>

                <div className="feature-item">
                  <h3>Audit trail</h3>
                  <p>Complete audit trail of all reconciliation activities for compliance and transparency.</p>
                </div>

                <div className="feature-item">
                  <h3>Exception reporting</h3>
                  <p>Detailed reports on unmatched transactions with suggested resolution actions.</p>
                </div>

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Supported Reconciliation Types */}
      <section className="supported-section">
        <div className="container">
          <div className="section-header">
            <h2>Reconciliation capabilities</h2>
            <p>Comprehensive bank reconciliation covering all types of financial transactions</p>
          </div>

          <div className="items-grid">
            <div className="item">
              <h3>Bank statements</h3>
              <ul>
                <li>Automated bank statement import</li>
                <li><Link to="/bank-integration">Multiple file format support</Link></li>
                {/* <li>Real-time statement processing</li> */}
                <li>Statement validation and verification</li>
                <li>Duplicate transaction detection</li>
                <li>Statement balance verification</li>
              </ul>
            </div>

            <div className="item">
              <h3>Payment transactions</h3>
              <ul>
                <li>Outgoing payment matching</li>
                <li>Supplier payment reconciliation</li>
                <li>Salary and payroll matching</li>
                <li>Employee expenses and reimbursements</li>
              </ul>
            </div>

            <div className="item">
              <h3>Receipt transactions</h3>
              <ul>
                <li>Customer payment matching</li>
                <li>Sales receipt reconciliation</li>
                <li>Refund transaction handling</li>
                <li>Investment income reconciliation</li>
              </ul>
            </div>

            <div className="item">
              <h3>Special transactions</h3>
              <ul>
                <li>Bank charges and fees</li>
                <li>Interest income transactions</li>
                <li>Cash collection transactions</li>
                {/* <li>Currency exchange transactions</li> */}
                {/* <li>Transfer between accounts</li> */}
                {/* <li>Standing orders and direct debits</li> */}
                <li>POS and credit card transactions</li>
                <li>Other - <Link to="/contact">Contact us</Link> to find out more</li>
                {/* <li>Investment transactions</li> */}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Reconciliation Process */}
      <section className="implementation-process">
        <div className="container">
          <div className="section-header">
            <h2>Reconciliation process</h2>
            <p>Our streamlined process ensures accurate and efficient bank reconciliation</p>
          </div>

          <div className="process-steps five-steps">
            <div className="process-step">
              <div className="step-number">1</div>
              <div className="step-content">
                <h3>Data import</h3>
                <p>Import bank statements and transaction data from various sources and formats.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">2</div>
              <div className="step-content">
                <h3>Automatic matching</h3>
                <p>Apply intelligent matching algorithms to automatically reconcile transactions.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">3</div>
              <div className="step-content">
                <h3>Exception review</h3>
                <p>Review and resolve unmatched transactions with guided exception handling.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">4</div>
              <div className="step-content">
                <h3>Validation & approval</h3>
                <p>Validate reconciliation results and approve for final posting to your books.</p>
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">5</div>
              <div className="step-content">
                <h3>Reporting & audit</h3>
                <p>Generate reconciliation reports and maintain complete audit trails.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Ready to automate your reconciliation?</h2>
            <p>
              Save time and ensure accuracy with intelligent bank reconciliation.
              Contact us today to discuss your reconciliation needs.
            </p>
            <Link to="/onboarding" className="btn btn-secondary btn-large">Get started</Link>
          </div>
        </div>
      </section>

      {/* Copyright Section */}
      <Copyright />
    </div>
  );
};

export default AutomaticBankReconciliationPage;
